export interface BlogPost {
  blogId?: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  featured: boolean;
  tags: string[];
  createdAt?: string;
  updatedAt?: string;
}

interface ApiResponse<T> {
  isSuccess: boolean;
  data: T;
  message: string;
}

const APP_ENV = (import.meta.env.VITE_APP_ENV || 'dev').toLowerCase();
export const API_BASE_URL =
  (APP_ENV === 'prod' ? import.meta.env.VITE_API_URL_PROD : import.meta.env.VITE_API_URL_DEV) ||
  import.meta.env.VITE_API_URL ||
  'https://api.avachatai.com/api';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export const categoryColors: Record<string, string> = {
  'Mental Health': '#7C3AED',
  'AI & Technology': '#0891b2',
  'Emotional Wellness': '#e11d48',
  Relationships: '#d97706',
  'Self-Care': '#059669',
};

export async function fetchBlogs(params?: {
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<BlogPost[]> {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.search) query.set('search', params.search);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));

  const url = `${API_BASE_URL}/blog${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch blogs');
  }
  const result: ApiResponse<{ posts: BlogPost[] }> = await response.json();
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to fetch blogs');
  }
  return (result.data.posts || []).map((post) => ({
    ...post,
    image:
      post.image && post.image.startsWith('/public/')
        ? `${ASSET_BASE_URL}${post.image}`
        : post.image,
  }));
}

export async function fetchBlogBySlug(slug: string): Promise<BlogPost | null> {
  const response = await fetch(`${API_BASE_URL}/blog/${slug}`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error('Failed to fetch blog detail');
  }
  const result: ApiResponse<BlogPost> = await response.json();
  if (!result.isSuccess) {
    throw new Error(result.message || 'Failed to fetch blog detail');
  }
  const post = result.data;
  return {
    ...post,
    image: post.image && post.image.startsWith('/public/') ? `${ASSET_BASE_URL}${post.image}` : post.image,
  };
}
